import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useAuth, pickPostLoginPath } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import PasswordInput from "@/components/PasswordInput";
import PublicPageShell from "@/components/PublicPageShell";
import FieldError from "@/components/a11y/FieldError";
import PoliteStatus from "@/components/a11y/PoliteStatus";
import LoginWallContestContext from "@/components/LoginWallContestContext";
import { Loader2 } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Backlog B.2.x — safe redirect-target validator.
 *
 * `?next=` MUST be a same-origin path (starts with "/", does NOT start
 * with "//" or contain ":" anywhere — which would let an attacker pass
 * a full off-origin URL like `next=https://evil.com`). If the value
 * fails any gate, the helper returns null and the caller falls back to
 * the role-based dashboard redirect. This is the standard
 * open-redirect mitigation; same posture every framework uses.
 */
function safeNext(raw) {
  if (!raw || typeof raw !== "string") return null;
  if (!raw.startsWith("/")) return null;
  if (raw.startsWith("//")) return null;          // protocol-relative URL escape
  if (raw.includes(":")) return null;             // any scheme/port specifier
  // Cap length so we don't carry around megabyte URLs.
  if (raw.length > 512) return null;
  return raw;
}

export default function Login() {
  const { setSession, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextParam = safeNext(searchParams.get("next"));
  // Dispatch A (Feb 2026) — contest-context framing when the user
  // arrived here from `/contest/enter`. New users overwhelmingly
  // dominate this funnel, so we promote the sign-up path to
  // primary and demote the login form to a secondary affordance.
  // No copy leak when `next` is anything else — default flow
  // remains byte-identical.
  const isContestEntryNext = nextParam === "/contest/enter";
  const signupHref = isContestEntryNext
    ? "/signup?next=%2Fcontest%2Fenter"
    : "/signup";

  // Commit B — §4.1 funnel step. `contest.login_wall_hit` fires
  // exactly once on mount of this page when the caller arrived
  // here from /contest/enter. Fires whether the user chose the
  // login form or the sign-up path — the "wall hit" is the shared
  // event that both branches share.
  useEffect(() => {
    if (isContestEntryNext) {
      trackEvent("contest.login_wall_hit");
    }
    // Mount-only — nextParam is stable within a single render lifecycle
    // (URL is captured on first render). Re-navigation replaces the
    // component and re-fires the effect at the next mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // Phase L.2 P1 — persistent inline error surface (audit LG-1).
  // Toast fires in parallel for the visual "just-happened" cue, but
  // a stable `<FieldError role="alert">` region gives SR users an
  // error they can revisit after the toast auto-dismisses.
  const [formError, setFormError] = useState(null);

  // Capability-aware redirect: if a safe `?next=` is present, send the
  // user back there. Else fall back to the role-based dashboard.
  // Backlog B.2.x — used by the `/review/{booking_id}` flow so an
  // unauthenticated click on the email CTA bounces through login and
  // lands on the review page automatically (no second click required).
  const redirectByCapability = (u) => {
    if (nextParam) {
      navigate(nextParam, { replace: true });
      return;
    }
    navigate(pickPostLoginPath(u) || "/", { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      const msg = "Please enter a valid email address.";
      setFormError(msg);
      toast.error(msg);
      return;
    }
    if (!password) {
      const msg = "Password is required.";
      setFormError(msg);
      toast.error(msg);
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setSession(data.access_token, data.user);
      await refreshMe();
      toast.success(`Welcome back, ${data.user.name || data.user.email}!`);
      redirectByCapability(data.user);
    } catch (err) {
      const msg =
        formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    // Phase 4b Step 1 — Google OAuth Authorization Code flow.
    // Frontend kicks off the redirect; the backend issues a signed
    // state JWT and constructs the Google authorize URL. On callback
    // (`/auth/google`) the frontend verifies state matches sessionStorage
    // and POSTs `{code, state}` to `/api/auth/google` to obtain our JWT.
    // Mock mode (default in dev / tests) returns a fake Google URL that
    // the test client doesn't actually follow — the legacy `{id_token}`
    // path remains supported on the backend if anyone needs to drive
    // mock signups directly via curl.
    setGoogleLoading(true);
    try {
      const { data } = await api.get("/auth/google/redirect-url");
      if (!data?.state || !data?.redirect_url) {
        throw new Error("Missing state or redirect_url from /auth/google/redirect-url");
      }
      sessionStorage.setItem("pp_google_oauth_state", data.state);
      window.location.assign(data.redirect_url);
    } catch (err) {
      const detail = err.response?.data;
      // 503 creds_missing — surface a clean message.
      if (err.response?.status === 503 && detail?.google_code === "creds_missing") {
        toast.error("Google sign-in is not configured yet. Please use email + password.");
      } else {
        toast.error(formatApiErrorDetail(detail?.detail) || err.message);
      }
      setGoogleLoading(false);
    }
  };

  return (
    <PublicPageShell mainClass="flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <Link to="/" data-testid="login-logo-link" className="block">
            <Logo variant="full" className="h-32 w-32" />
          </Link>
          <h1
            className="mt-2 font-heading text-3xl font-bold text-pp-text"
            data-testid="login-hero-heading"
          >
            {isContestEntryNext ? "One quick account" : "Welcome back"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isContestEntryNext
              ? "Sign up in seconds to enter your pup."
              : "Log in to keep your yard fresh."}
          </p>
        </div>

        {isContestEntryNext && <LoginWallContestContext />}

        <div className="rounded-3xl border border-border bg-pp-surface p-8 shadow-sm">
          {/* Phase L.2 P3 — audit LG-3. sr-only intermediate H2 so
              the H1 (hero above) → H3 (SharedFooter Product/Legal
              columns) heading hierarchy doesn't skip a level for
              screen-reader users navigating by heading. */}
          <h2 className="sr-only" data-testid="login-form-h2">
            Log in to PooPluckers
          </h2>

          {isContestEntryNext && (
            <>
              {/* Dispatch A (Feb 2026) — primary sign-up CTA above
                  the auth form when the caller arrived from
                  `/contest/enter`. Same visual weight as the login
                  submit button below (h-12, rounded-full, bg-pp-purple)
                  so it reads as THE primary action; the login form
                  itself becomes the secondary path via the "Already
                  have an account?" divider that follows. */}
              <Link
                to={signupHref}
                className="mb-4 flex h-12 w-full items-center justify-center rounded-full bg-pp-purple text-base font-semibold text-white hover:bg-pp-purple-dark"
                data-testid="login-wall-signup-primary-cta"
              >
                Sign up to enter your pup
              </Link>
              <div className="my-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span
                  className="text-xs uppercase tracking-wider text-muted-foreground"
                  data-testid="login-wall-already-have-account-divider"
                >
                  Already have an account?
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>
            </>
          )}

          {/* MOCKED: Google OAuth button — Phase 1 stub */}
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogle}
            disabled={googleLoading}
            className="h-12 w-full rounded-full border-border bg-pp-surface text-pp-text hover:bg-secondary"
            data-testid="login-google-button"
          >
            {googleLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.85 0-5.27-1.92-6.13-4.51H2.18v2.84A11 11 0 0 0 12 23z" />
                <path fill="#FBBC05" d="M5.87 14.14a6.6 6.6 0 0 1 0-4.28V7.02H2.18a11 11 0 0 0 0 9.96l3.69-2.84z" />
                <path fill="#EA4335" d="M12 5.25c1.62 0 3.06.56 4.21 1.65l3.15-3.15C17.45 1.98 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.02l3.69 2.84C6.73 7.17 9.15 5.25 12 5.25z" />
              </svg>
            )}
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {/* Phase L.2 P1 — persistent form-level error announcement
                (audit LG-1). Non-ephemeral SR alert; toast fires in
                parallel for the visual cue. */}
            <FieldError
              id="login-form-error"
              message={formError}
              testId="login-form-error"
              className="mt-0"
            />
            <div>
              <Label htmlFor="email" className="text-sm font-semibold text-pp-text">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-2 h-12 rounded-full border-border bg-pp-surface px-5"
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-semibold text-pp-text">
                Password
              </Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                className="mt-2"
                required
                data-testid="login-password-input"
              />
              <div className="mt-2 flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm font-semibold text-brand-orange hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="h-12 w-full rounded-full bg-pp-purple text-base text-white hover:bg-pp-purple-dark"
              data-testid="login-submit-button"
              aria-busy={submitting || undefined}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  Logging in…
                </>
              ) : (
                "Log in"
              )}
            </Button>
            {/* Phase L.2 P2 — audit LG-2. Polite live region so
                assistive-tech gets the submit-in-progress + settled
                announcement. Region stays mounted (empty when idle). */}
            <PoliteStatus
              visuallyHidden
              message={submitting ? "Logging you in…" : ""}
              testId="login-submit-status"
            />
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            New here?{" "}
            <Link
              to={signupHref}
              className="font-semibold text-brand-orange hover:underline"
              data-testid="login-signup-link"
            >
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </PublicPageShell>
  );
}
